import { useAtomValue } from 'jotai';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import { opalAuthAtom } from '@frontend/atoms';
import { OKTA_LOGOUT_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import { optiGlobalHeaderStyles, systemComponentSlotStyle } from './OptiGlobalHeader.css';

type OptiGlobalHeaderProps = {
  visible?: boolean;
  children?: ReactNode;
};

export function OptiGlobalHeader({ visible = true, children }: OptiGlobalHeaderProps) {
  const { authState, oktaAuth } = useOktaAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const { instanceId } = useInstanceId();
  const opalAuth = useAtomValue(opalAuthAtom);
  const onLogout = useCallback(() => {
    (opalAuth?.logout() ?? Promise.resolve())
      .catch(() => {})
      .finally(() =>
        oktaAuth
          .signOut({
            revokeAccessToken: true,
            revokeRefreshToken: true,
            postLogoutRedirectUri: OKTA_LOGOUT_URL,
          })
          .catch(err => sendSentryError(err))
      );
  }, [oktaAuth, opalAuth]);

  useEffect(() => {
    const currentHeaderRef = headerRef.current;
    currentHeaderRef?.addEventListener?.('@opti-common-header:logout', onLogout);

    return () => {
      currentHeaderRef?.removeEventListener?.('@opti-common-header:logout', onLogout);
    };
  }, [headerRef, onLogout]);
  if (!instanceId) {
    return null;
  }
  return (
    <div className={visible ? optiGlobalHeaderStyles.visible : optiGlobalHeaderStyles.hidden}>
      <opti-common-header
        ref={headerRef}
        auth-token={authState?.accessToken?.accessToken}
        product-name="Opal"
        instance-id={instanceId}
      >
        {children && (
          <div slot="system-component" className={systemComponentSlotStyle}>
            {children}
          </div>
        )}
      </opti-common-header>
    </div>
  );
}
